// Тонкий fetch-клиент для общения с нашим Express API.
//
// Обеспечивает:
//   - всегда credentials:'include' — сессионная cookie ходит автоматически
//   - JSON request/response, автоматический Content-Type
//   - стабильный объект ошибки ApiError(status, code, message, body)
//   - корректную работу AbortSignal (для cancellation в TanStack Query)
//
// Серверные ответы бэка (см. api/*.js) всегда имеют форму:
//   ok:    200/201 + JSON-payload
//   err:   4xx/5xx + { message: '...', code: 'snake_case_code' }
// Этот контракт здесь и обрабатывается.

export class ApiError extends Error {
    constructor({ status, code, message, body }) {
        super(message || `HTTP ${status}`);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
        this.body = body;
    }
}

async function request(path, { method = 'GET', body, signal, headers = {} } = {}) {
    const init = {
        method,
        credentials: 'include',
        headers: {
            Accept: 'application/json',
            ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
            ...headers,
        },
        signal,
    };
    if (body !== undefined) {
        init.body = JSON.stringify(body);
    }

    let res;
    try {
        res = await fetch(path, init);
    } catch (err) {
        if (err?.name === 'AbortError') throw err;
        // Сеть/CORS/DNS — формируем единообразную ошибку
        throw new ApiError({
            status: 0,
            code: 'network_error',
            message: 'Не удалось связаться с сервером',
            body: null,
        });
    }

    let parsed = null;
    const contentType = res.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
        try {
            parsed = await res.json();
        } catch {
            // невалидный JSON — оставляем parsed = null
        }
    }

    if (!res.ok) {
        // Глобальный сигнал «сессия истекла»: AuthProvider слушает 'auth-expired'
        // и переводит контекст в guest, ProtectedRoute сам редиректит на /login.
        // Не ругаемся на первый /me при загрузке — он специально допускает 401
        // (через try/catch в refresh()), поэтому event просто синхронизирует контекст.
        if (res.status === 401 && typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('auth-expired'));
        }
        throw new ApiError({
            status: res.status,
            code: parsed?.code || 'http_error',
            message: parsed?.message || `Ошибка сервера (HTTP ${res.status})`,
            body: parsed,
        });
    }

    return parsed;
}

export const api = {
    get: (path, opts) => request(path, { ...opts, method: 'GET' }),
    post: (path, body, opts) => request(path, { ...opts, method: 'POST', body }),
    put: (path, body, opts) => request(path, { ...opts, method: 'PUT', body }),
    patch: (path, body, opts) => request(path, { ...opts, method: 'PATCH', body }),
    delete: (path, opts) => request(path, { ...opts, method: 'DELETE' }),
};
